import React, { useState, useEffect, useRef } from "react";
import { LoadingButton } from "@mui/lab";

const MODE_TO_VARIANT = {
  contained: "contained",
  outlined: "outlined",
  text: "text",
};

const DEFAULT_SX = {
  mx: 5,
  mt: 1.25,
  mb: 2.5,
};

const CustomDebouncedButton = ({
  buttonText,
  children,
  onPress,
  onClick,
  debounceTime = 2000,
  buttonStyle,
  sx,
  labelStyle,
  disabled = false,
  mode = "contained",
  variant,
  icon,
  startIcon,
  loading = false,
  compact = false,
  uppercase = false,
  contentStyle,
  size,
  ...otherProps
}) => {
  const [isDebouncing, setIsDebouncing] = useState(false);
  const timeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const handleClick = (event) => {
    if (isDebouncing || disabled || loading) return;

    const handler = onPress ?? onClick;
    handler?.(event);

    if (debounceTime > 0) {
      setIsDebouncing(true);
      timeoutRef.current = setTimeout(() => {
        setIsDebouncing(false);
        timeoutRef.current = null;
      }, debounceTime);
    }
  };

  const resolvedVariant = variant ?? MODE_TO_VARIANT[mode] ?? "contained";
  const resolvedStartIcon = startIcon ?? icon;
  const label = buttonText ?? children;

  return (
    <LoadingButton
      variant={resolvedVariant}
      onClick={handleClick}
      disabled={isDebouncing || disabled || loading}
      loading={loading}
      size={compact ? "small" : size}
      startIcon={resolvedStartIcon}
      sx={{
        ...DEFAULT_SX,
        ...(uppercase ? { textTransform: "uppercase" } : {}),
        ...labelStyle,
        ...buttonStyle,
        ...contentStyle,
        ...sx,
      }}
      {...otherProps}
    >
      {label}
    </LoadingButton>
  );
};

export default CustomDebouncedButton;
export { CustomDebouncedButton };
